import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import { registerWorker } from './push';
import { watchForInstallPrompt } from './install/prompt';
import { countArrival } from './install/report';
import './styles.css';

/*
 * Before React, and before any session is known.
 *
 * Chrome offers to install a site only once a service worker with a `fetch` handler is
 * registered, so registering it from inside the notifications panel meant the install prompt
 * arrived after signing in — useless to a learner whose only way of being reached is push,
 * who needs the app on their home screen before they have an account. See `registerWorker`;
 * it cannot throw and is not awaited, so nothing below waits on it and no browser that
 * refuses service workers loses the site.
 */
registerWorker();

/*
 * Also before React, and for a sharper version of the same reason (F-121).
 *
 * Chromium fires `beforeinstallprompt` once, as soon as it decides the site qualifies, and an
 * unhandled event is *gone* — taking with it the only genuine one-tap install this app has.
 * On a warm load that moment is before any component has mounted, so a listener attached
 * inside the screen that shows the button would never see it.
 */
watchForInstallPrompt();

/*
 * Also before React, and for the reason the other two are (F-123).
 *
 * On iOS an install produces no event whatsoever — WebKit has no install API and fires
 * nothing — so the only evidence one happened is this: a launch arriving in standalone mode,
 * in a session that has nothing to do with the one that made the offer, quite possibly before
 * any account exists. Counting it from inside a component would mean counting only the
 * learners who reach that particular screen, and counting it after the session has been
 * resolved would mean losing everybody who installed the app before signing up — which is
 * most of the point, since push is the only way this product reaches anybody and a home
 * screen is the only way iOS allows push.
 *
 * Cannot throw and is not awaited, exactly like `registerWorker` above: a visitor whose count
 * did not land must still get the product.
 */
countArrival();

const root = document.getElementById('root');
if (!root) throw new Error('#root is missing from index.html');

createRoot(root).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
